import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SafetyNotice } from "@/components/SafetyNotice";
import { KernAntwort } from "@/components/KernAntwort";
import { ProgressHint } from "@/components/ProgressHint";
import { sitzungsKennung } from "@/lib/sitzung";

const useNoIndex = (title) => useEffect(() => {
  const prev = document.title;
  document.title = title;
  const m = document.createElement("meta");
  m.name = "robots";
  m.content = "noindex, nofollow";
  document.head.appendChild(m);
  return () => { m.remove(); document.title = prev; };
}, [title]);

const useZugang = () => {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    // Ohne Token: erlaubt, sobald die Tuer oeffentlich ist (Server entscheidet).
    const p = new URLSearchParams(window.location.search).get("zugang") || sessionStorage.getItem("tuer_zugang") || "";
    api.get("/tuer/zugang", { headers: { "X-Tuer-Zugang": p } })
      .then(() => { if (p) sessionStorage.setItem("tuer_zugang", p); setOk(p); })
      .catch(() => { sessionStorage.removeItem("tuer_zugang"); setOk(false); });
  }, []);
  return ok;
};

const NichtFreigegeben = () => (
  <main className="min-h-screen bg-[#FAF7F2] px-5 py-16" data-testid="tuer-gesperrt">
    <div className="mx-auto max-w-xl">
      <h1 className="text-3xl font-extrabold text-slate-900">Diese Seite ist noch nicht freigegeben.</h1>
      <Link to="/" className="mt-6 inline-block font-semibold underline" data-testid="tuer-gesperrt-home">Zur Startseite</Link>
    </div>
  </main>
);

export default function TuerEinschaetzung() {
  useNoIndex("Flex4Success – Einschätzung");
  const zugang = useZugang();
  if (zugang === null) return <main className="min-h-screen bg-[#FAF7F2]" data-testid="tuer-laedt" />;
  if (zugang === false) return <NichtFreigegeben />;
  return <TuerFormular zugang={zugang} />;
}

function TuerFormular({ zugang }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [limit, setLimit] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setRes(null);
    try {
      const { data } = await api.post("/tuer/einschaetzung", { situation: text }, { headers: { "X-Tuer-Zugang": zugang, "X-Sitzung": sitzungsKennung() } });
      setRes(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Das hat nicht geklappt. Bitte versuche es noch einmal.");
      setLimit(err?.response?.status === 429);
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#FAF7F2] px-5 py-10" data-testid="tuer-page">
      <div className="mx-auto max-w-xl">
        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight text-slate-900">Beschreib eine Situation, die dich gerade beschäftigt.</h1>
        <p className="mt-3 text-base text-slate-600">Ein, zwei Sätze reichen. Du bekommst eine Einschätzung und einen Satz, den du sagen kannst.</p>
        <form onSubmit={submit} className="mt-6">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000}
            placeholder="z. B. Meine Kollegin übergeht mich in Meetings …"
            className="w-full rounded-xl border border-slate-300 bg-white p-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            data-testid="tuer-input" />
          <button type="submit" disabled={busy || text.trim().length < 10}
            className="mt-3 w-full min-h-[52px] rounded-2xl bg-[#F3651D] px-6 text-lg font-bold text-white hover:bg-[#d9551a] disabled:opacity-60"
            data-testid="tuer-submit">{busy ? "Einen Moment …" : "Einschätzung bekommen"}</button>
        </form>
        {busy && <ProgressHint />}
        {error && <p role="alert" className={`mt-4 text-sm font-semibold ${limit ? "text-slate-700" : "text-red-700"}`} data-testid="tuer-error">{error}</p>}
        {res?.status === "sicherheit" && <div className="mt-8"><SafetyNotice data={res} /></div>}
        {res?.status === "ok" && (
          <div className="mt-8 rounded-2xl bg-white p-6 shadow-sm" data-testid="tuer-result">
            <KernAntwort antwort={res.antwort} />
          </div>
        )}
      </div>
    </main>
  );
}
